import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Package, Truck, Clock, Loader2, ArrowRight, CheckCircle } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useCartContext } from '../contexts/CartContext';
import { ProxyImage } from '../lib/imageProxy';

interface CartItem {
  id: string;
  product_id: string;
  quantity: number;
  price_type?: 'contado' | 'credito' | 'mayorista' | 'publico';
  product: {
    name: string;
    base_price: number;
    prices?: {
      price_type?: 'contado' | 'credito' | 'mayorista' | 'publico';
      min_quantity: number;
      price: number;
      offer_price?: number;
    }[];
    main_image: string;
  };
}

export default function OrderCheckout() {
  const navigate = useNavigate();
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [userPriceType, setUserPriceType] = useState<'contado' | 'credito' | 'mayorista' | 'publico' | null>(null);
  const { refreshCart } = useCartContext();
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    fetchUserPriceType();
    fetchCartItems();
  }, []);

  const fetchUserPriceType = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: profile, error } = await supabase
        .from('profiles')
        .select('price_type')
        .eq('id', user.id)
        .single();

      if (error) throw error;
      console.log('Tipo de precio del usuario cargado en checkout:', profile?.price_type);
      setUserPriceType(profile?.price_type || null);
    } catch (err) {
      console.error('Error fetching user price type:', err);
    }
  };

  const fetchCartItems = async () => {
    try {
      setLoading(true);
      
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        navigate('/login');
        return;
      }

      // Obtener el carrito del usuario
      const { data: cart, error: cartError } = await supabase
        .from('cart')
        .select('id')
        .eq('user_id', user.id)
        .single();

      if (cartError) {
        console.error('Error al obtener el carrito:', cartError);
        throw cartError;
      }

      if (!cart) {
        throw new Error('No se encontró el carrito');
      }

      // Obtener los items del carrito
      const { data: items, error: itemsError } = await supabase
        .from('cart_items')
        .select(`
          id,
          quantity,
          product_id,
          price_type,
          product:products(
            name,
            base_price,
            prices:product_prices(
              price_type,
              min_quantity,
              price,
              offer_price
            ),
            main_image
          )
        `)
        .eq('cart_id', cart.id);

      if (itemsError) {
        console.error('Error al obtener los items del carrito:', itemsError);
        throw itemsError;
      }

      if (!items || items.length === 0) {
        navigate('/');
        return;
      }

      console.log('Items obtenidos del carrito:', JSON.stringify(items, null, 2));

      // Transformar los datos para que coincidan con la interfaz CartItem
      const transformedItems: CartItem[] = items.map(item => {
        // Verificar que item.product tenga el formato correcto
        if (!item.product || typeof item.product !== 'object') {
          console.error('Error: item.product no tiene el formato esperado', item);
          return {
            id: item.id,
            product_id: item.product_id,
            quantity: item.quantity,
            price_type: item.price_type,
            product: {
              name: 'Producto no disponible',
              base_price: 0,
              prices: [],
              main_image: ''
            }
          };
        }

        return {
          id: item.id,
          product_id: item.product_id,
          quantity: item.quantity,
          price_type: item.price_type,
          product: {
            name: (item.product as any).name,
            base_price: (item.product as any).base_price,
            prices: (item.product as any).prices,
            main_image: (item.product as any).main_image
          }
        };
      });

      setCartItems(transformedItems);
    } catch (error) {
      console.error('Error fetching cart items:', error);
      setError('Error al cargar los productos del carrito');
    } finally {
      setLoading(false);
    }
  };

  const getPriceInfo = (item: CartItem) => {
    // Usar el tipo de precio del usuario actual, no el guardado en el item
    const type = userPriceType || 'publico';
    if (!item.product.prices || item.product.prices.length === 0) {
      return { current: item.product.base_price, original: null } as { current: number; original: number | null };
    }
    const applicable = item.product.prices.filter(p => p.price_type === type);
    if (applicable.length === 0) {
      return { current: item.product.base_price, original: null };
    }
    const valid = applicable.filter(p => item.quantity >= p.min_quantity);
    // Seleccionar el rango con min_quantity más alto que aplique (ordenar descendente)
    const selected = valid.length > 0
      ? [...valid].sort((a, b) => b.min_quantity - a.min_quantity)[0]
      : [...applicable].sort((a, b) => a.min_quantity - b.min_quantity)[0];
    if (selected.offer_price != null) {
      return { current: selected.offer_price, original: selected.price };
    }
    return { current: selected.price, original: null };
  };

  const getProductPrice = (item: CartItem) => {
    return getPriceInfo(item).current;
  };

  const getCartTotal = () => {
    return cartItems.reduce((sum, item) => 
      sum + (getProductPrice(item) * item.quantity), 0
    );
  };

  const handleCreateOrder = async () => {
    try {
      setIsSubmitting(true);
      setSuccessMessage(null);
      
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        navigate('/login');
        return;
      }

      // Verificar que hay items en el carrito
      if (cartItems.length === 0) {
        setError('No hay productos en el carrito');
        return;
      }

      // Mostrar mensaje de éxito
      setSuccessMessage('¡Procediendo al checkout! Redirigiendo...');
      
      // Añadir un pequeño retraso para mostrar el mensaje
      setTimeout(() => {
        // Redirigir al nuevo checkout
        navigate('/checkout');
      }, 1500);
      
    } catch (err) {
      console.error('Error al proceder al checkout:', err);
      setError('Error al proceder al checkout');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error || cartItems.length === 0) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="text-center">
          <h2 className="text-2xl font-semibold mb-4">Error</h2>
          <p className="text-gray-600 mb-8">{error || 'No hay productos en el carrito'}</p>
          <button
            onClick={() => navigate('/')}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700"
          >
            Volver al inicio
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      {successMessage && (
        <div className="fixed inset-x-0 top-4 flex justify-center z-50">
          <div className="bg-green-100 border border-green-200 text-green-800 px-4 py-3 rounded-lg shadow-md flex items-center gap-2 max-w-md">
            <CheckCircle className="h-5 w-5 text-green-500" />
            <span>{successMessage}</span>
          </div>
        </div>
      )}
      
      <div className="max-w-7xl mx-auto px-4">
        <div className="grid md:grid-cols-3 gap-8">
          {/* Resumen del pedido */}
          <div className="md:col-span-2 space-y-6">
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-lg font-semibold mb-4">Resumen del Pedido</h2>
              <div className="space-y-4">
                {cartItems.map((item) => (
                  <div key={item.id} className="flex items-center gap-4 py-4 border-b last:border-0">
                    <ProxyImage
                      src={item.product.main_image}
                      alt={item.product.name}
                      className="w-20 h-20 object-cover rounded-lg"
                    />
                    <div className="flex-1">
                      <h3 className="font-medium">{item.product.name}</h3>
                      <p className="text-sm text-gray-600">Cantidad: {item.quantity}</p>
                      <p className="text-sm font-medium">
                        {(() => {
                          const info = getPriceInfo(item);
                          const total = info.current * item.quantity;
                          return info.original ? (
                            <>
                              <span className="line-through text-xs text-gray-400 mr-1">Q{(info.original * item.quantity).toFixed(2)}</span>
                              <span className="text-red-600">Q {total.toFixed(2)}</span>
                              <span className="ml-1 px-1.5 py-0.5 bg-red-100 text-red-600 text-[10px] rounded uppercase font-semibold">Oferta</span>
                            </>
                          ) : (
                            <>Q {total.toFixed(2)}</>
                          );
                        })()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-lg font-semibold mb-4">Información Importante</h2>
              <div className="space-y-4 text-sm text-gray-600">
                <div className="flex items-center gap-3">
                  <Package className="w-5 h-5 text-blue-600" />
                  <p>Nuestro equipo verificará la disponibilidad de los productos solicitados.</p>
                </div>
                <div className="flex items-center gap-3">
                  <Clock className="w-5 h-5 text-blue-600" />
                  <p>El tiempo de revisión es de aproximadamente 24 horas hábiles.</p>
                </div>
                <div className="flex items-center gap-3">
                  <Truck className="w-5 h-5 text-blue-600" />
                  <p>Los costos de envío se calcularán una vez aprobado el pedido.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Total y acciones */}
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-lg font-semibold mb-4">Total del Pedido</h2>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Subtotal</span>
                  <span>Q {getCartTotal().toFixed(2)}</span>
                </div>
                <div className="flex justify-between pt-4 border-t text-base font-medium">
                  <span>Total</span>
                  <div className="text-right">
                    <div>Q {getCartTotal().toFixed(2)}</div>
                    <div className="text-xs text-gray-500">
                      Incluye Q {(getCartTotal() * 0.12).toFixed(2)} de impuestos
                    </div>
                  </div>
                </div>
              </div>

              <button
                onClick={handleCreateOrder}
                disabled={isSubmitting}
                className="w-full mt-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Procesando...
                  </>
                ) : (
                  'Proceder al Checkout'
                )}
              </button>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-6">
              <Link
                to="/"
                className="flex items-center justify-center gap-2 text-blue-600 hover:text-blue-700"
              >
                <span>Continuar comprando</span>
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
} 